import React, { useCallback, useMemo, useRef, useState } from 'react';

import {
  DndContext,
  type DragCancelEvent,
  type DragEndEvent,
  type DragStartEvent,
  PointerSensor,
  type PointerSensorOptions,
  type UniqueIdentifier,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  type UseSortableArguments,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { cn } from 'common/style/utils';
import VStack from 'common/uiV3/VStack';
import { type ListItemComponentType, SortableListItem } from './SortableListItem';

const defaultPointerSensorOptions: PointerSensorOptions = {
  activationConstraint: {
    distance: 8,
  },
};

export type SortableListProps<T, TElement extends HTMLElement = HTMLLIElement> = {
  items: T[];
  onItemsChanged(items: T[]): void;
  getId: (item: T) => UniqueIdentifier;
  ItemComponent: ListItemComponentType<T, TElement>;
  pointerSensorOptions?: PointerSensorOptions;
  disabled?: boolean;
  sortableProps?: Omit<UseSortableArguments, 'id'>;
  dndContextProps?: React.ComponentProps<typeof DndContext>;
  className?: string;
};

export function SortableList<T, TElement extends HTMLElement = HTMLLIElement>({
  items,
  onItemsChanged,
  getId,
  ItemComponent,
  pointerSensorOptions,
  disabled = false,
  sortableProps,
  dndContextProps,
  className,
}: SortableListProps<T, TElement>) {
  const sensors = useSensors(
    useSensor(PointerSensor, pointerSensorOptions ?? defaultPointerSensorOptions),
  );

  const itemIds = useMemo(() => items.map(getId), [items, getId]);

  const itemsRef = useRef(items);
  itemsRef.current = items;

  const itemIdsRef = useRef(itemIds);
  itemIdsRef.current = itemIds;

  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = useCallback((_event: DragStartEvent) => {
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setIsDragging(false);

      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const currentItems = itemsRef.current;
      const currentItemIds = itemIdsRef.current;

      const oldIndex = currentItemIds.indexOf(active.id);
      const newIndex = currentItemIds.indexOf(over.id);

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const newItems = arrayMove(currentItems, oldIndex, newIndex);
        onItemsChanged(newItems);
      }
    },
    [onItemsChanged],
  );

  const handleDragCancel = useCallback((_event: DragCancelEvent) => {
    setIsDragging(false);
  }, []);

  return (
    <VStack
      className={cn(
        {
          '[&_*]:!cursor-grabbing': isDragging,
        },
        className,
      )}>
      <DndContext
        sensors={disabled ? undefined : sensors}
        collisionDetection={closestCenter}
        onDragStart={disabled ? undefined : handleDragStart}
        onDragEnd={disabled ? undefined : handleDragEnd}
        onDragCancel={disabled ? undefined : handleDragCancel}
        {...dndContextProps}>
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {items.map((item) => (
            <SortableListItem
              key={getId(item)}
              id={getId(item)}
              item={item}
              ItemComponent={ItemComponent}
              disabled={disabled}
              sortableProps={sortableProps}
            />
          ))}
        </SortableContext>
      </DndContext>
    </VStack>
  );
}
